import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Copy, Share2, MessageCircle, Check, PartyPopper, Trophy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (c) => `$${(c / 100).toFixed(2)}`;

export const KickbackShareCard = ({ orderId }) => {
  const [kb, setKb] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    axios.get(`${API}/kickbacks/order/${orderId}`).then(r => {
      if (r.data?.enabled) setKb(r.data);
    }).catch(() => {});
  }, [orderId]);

  const shareText = kb ? `I just got my ticket to ${kb.event_title} 🎉 Grab yours with my link: ${kb.share_url}` : '';

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(kb.share_url);
      setCopied(true);
      toast.success('Link copied — drop it in the group chat!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.info(kb.share_url);
    }
  }, [kb]);

  const nativeShare = useCallback(async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: kb.event_title, text: shareText, url: kb.share_url });
      } else {
        await copyLink();
      }
    } catch { /* user dismissed */ }
  }, [kb, shareText, copyLink]);

  if (!kb) return null;

  const { reward_model, threshold, referral_count, rewarded } = kb;
  const headline = reward_model === 'milestone_refund'
    ? (kb.ticket_value_cents > 0
        ? <>Going with friends? If <span className="text-amber-300">{threshold} friends</span> buy tickets, your ticket is <span className="text-amber-300">100% FREE</span> ({fmt(kb.ticket_value_cents)} auto-refunded to your card)</>
        : <>Bringing the crew? Get <span className="text-amber-300">{threshold} friends</span> in with your personal link below — everyone shows up together</>)
    : reward_model === 'cash_kickback'
      ? <>Earn <span className="text-amber-300">{fmt(kb.kickback_cents)} back</span> for every ticket a friend buys with your link — automatic refunds, up to your full ticket price</>
      : <>Refer <span className="text-amber-300">{threshold} friends</span> and unlock: <span className="text-amber-300">{kb.perk_label}</span> at the door</>;

  const pct = reward_model === 'cash_kickback'
    ? Math.min(100, kb.ticket_value_cents > 0 ? (kb.paid_cents / kb.ticket_value_cents) * 100 : 0)
    : Math.min(100, (referral_count / threshold) * 100);

  return (
    <div className="rounded-2xl p-[1.5px] mb-4" style={{ background: 'linear-gradient(135deg, #f59e0b, #ec4899, #8b5cf6)' }} data-testid="kickback-share-card">
      <div className="rounded-2xl bg-[#13101d] p-5">
        {rewarded ? (
          <div className="text-center mb-3" data-testid="kickback-rewarded">
            <Trophy className="h-8 w-8 text-amber-400 mx-auto mb-1" />
            <p className="font-bold text-amber-300">
              {reward_model === 'perk'
                ? `Perk unlocked: ${kb.perk_unlocked}`
                : (reward_model === 'cash_kickback' ? kb.paid_cents : kb.reward_amount_cents) > 0
                  ? `You earned ${fmt(reward_model === 'cash_kickback' ? kb.paid_cents : kb.reward_amount_cents)} back!`
                  : 'Goal reached — your friends are in!'}
            </p>
            <p className="text-xs text-white/60 mt-0.5">Keep sharing — your friends still get the easiest way in.</p>
          </div>
        ) : (
          <div className="flex items-start gap-3 mb-3">
            <PartyPopper className="h-6 w-6 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-semibold text-white leading-snug" data-testid="kickback-headline">{headline}</p>
          </div>
        )}

        {/* Progress */}
        <div className="mb-3">
          <div className="flex justify-between text-[11px] text-white/70 mb-1">
            <span data-testid="kickback-progress-label">
              {reward_model === 'cash_kickback'
                ? `${fmt(kb.paid_cents)} earned so far`
                : `${referral_count} of ${threshold} friends in`}
            </span>
            {kb.referrals.length > 0 && <span>{kb.referrals.map(r => r.name).slice(0, 3).join(', ')}{kb.referrals.length > 3 ? ` +${kb.referrals.length - 3}` : ''}</span>}
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #f59e0b, #ec4899)' }} />
          </div>
        </div>

        {/* Link + share buttons */}
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 mb-3">
          <p className="text-[11px] font-mono text-indigo-300 truncate flex-1" data-testid="kickback-share-url">{kb.share_url}</p>
          <button onClick={copyLink} className="text-white/70 hover:text-white flex-shrink-0" data-testid="kickback-copy-link" aria-label="Copy link">
            {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Button size="sm" className="bg-gradient-to-r from-amber-500 to-pink-500 text-white border-0 hover:opacity-90" onClick={nativeShare} data-testid="kickback-native-share">
            <Share2 className="h-3.5 w-3.5 mr-1.5" /> Share
          </Button>
          <Button size="sm" variant="outline" className="border-white/15 text-white hover:bg-white/10" asChild data-testid="kickback-whatsapp">
            <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> WhatsApp
            </a>
          </Button>
          <Button size="sm" variant="outline" className="border-white/15 text-white hover:bg-white/10" asChild data-testid="kickback-sms">
            <a href={`sms:?&body=${encodeURIComponent(shareText)}`}>
              <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> Text
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default KickbackShareCard;
